// Synthesis recipes for the eight ANYCAST voices. Everything is built from oscillators,
// a shared noise buffer, filters and envelopes: no samples. Each recipe writes into the
// `out` node that the engine hands it (the engine owns loudness, tone, pan and sends)
// and reports every node it created so the engine can tear the note down afterwards.

import type { VoiceName } from "../../shared/music";

export interface VoiceContext {
  ctx: BaseAudioContext;
  /** A couple of seconds of white noise, shared by every note. */
  noise: AudioBuffer;
}

export interface NoteBuild {
  sources: AudioScheduledSourceNode[];
  nodes: AudioNode[];
  /** Context time at which every source of the note has stopped. */
  end: number;
}

/**
 * @param f     fundamental in Hz
 * @param t     start time (context seconds)
 * @param vel   0..1, already clamped
 * @param bright 0..1 brightness (velocity, accent, age and pitch folded together)
 */
export type Recipe = (v: VoiceContext, out: AudioNode, f: number, t: number, vel: number, bright: number) => NoteBuild;

/** Small builder that remembers every node a note creates. */
class Note {
  readonly sources: AudioScheduledSourceNode[] = [];
  readonly nodes: AudioNode[] = [];
  end = 0;
  constructor(readonly v: VoiceContext, readonly t: number) {}

  get ctx(): BaseAudioContext {
    return this.v.ctx;
  }
  get nyquist(): number {
    return this.v.ctx.sampleRate * 0.45;
  }

  osc(type: OscillatorType, freq: number, detuneCents = 0): OscillatorNode {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, this.t);
    if (detuneCents) o.detune.setValueAtTime(detuneCents, this.t);
    this.sources.push(o);
    this.nodes.push(o);
    return o;
  }

  gain(value = 0): GainNode {
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(value, this.t);
    this.nodes.push(g);
    return g;
  }

  filter(type: BiquadFilterType, freq: number, q = 0.707): BiquadFilterNode {
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.setValueAtTime(Math.min(freq, this.nyquist), this.t);
    f.Q.setValueAtTime(q, this.t);
    this.nodes.push(f);
    return f;
  }

  noise(dur: number): AudioBufferSourceNode {
    const s = this.ctx.createBufferSource();
    s.buffer = this.v.noise;
    s.loop = true;
    this.sources.push(s);
    this.nodes.push(s);
    // Random offset so consecutive notes do not share the same grain.
    const off = Math.random() * Math.max(0, this.v.noise.duration - dur - 0.01);
    s.start(this.t, off);
    s.stop(this.t + dur + 0.02);
    return s;
  }

  /** Percussive envelope: linear attack to `peak`, exponential decay with time constant tau. */
  perc(g: GainNode, peak: number, attack: number, tau: number, at = this.t): number {
    const p = g.gain;
    p.setValueAtTime(0, at);
    p.linearRampToValueAtTime(peak, at + attack);
    p.setTargetAtTime(0, at + attack, tau);
    return at + attack + tau * 8;
  }

  /** A sine partial with its own percussive envelope, skipped if it would alias. */
  partial(out: AudioNode, freq: number, peak: number, attack: number, tau: number, type: OscillatorType = "sine"): void {
    if (freq >= this.nyquist || peak <= 0) return;
    const o = this.osc(type, freq);
    const g = this.gain();
    this.bump(this.perc(g, peak, attack, tau));
    o.connect(g).connect(out);
  }

  bump(end: number): void {
    if (end > this.end) this.end = end;
  }

  finish(): NoteBuild {
    const end = this.end + 0.05;
    for (const s of this.sources) {
      if (s instanceof OscillatorNode) {
        s.start(this.t);
        s.stop(end);
      }
    }
    return { sources: this.sources, nodes: this.nodes, end };
  }
}

const cents = (c: number) => Math.pow(2, c / 1200);

// ---------------------------------------------------------------------------------------
// glass: two-operator FM electric-piano / bell. A 1:1 pair gives a round Rhodes body,
// a 1:7 pair gives the glassy tine on the attack; the modulation indices decay so the
// tail relaxes towards a pure tone. A slightly detuned third carrier adds shimmer.
const glass: Recipe = (v, out, f, t, vel, bright) => {
  const n = new Note(v, t);
  const amp = n.gain();
  // Long tail, a little shorter for higher notes so the top end does not pile up.
  const tau = Math.min(1, Math.max(0.35, 0.8 * Math.pow(262 / f, 0.3)));
  const tail = n.perc(amp, 1, 0.004, tau);
  n.bump(tail);
  amp.connect(out);

  // Body: carrier f, modulator f, index 1.6 -> 0.25.
  const c1 = n.osc("sine", f);
  const m1 = n.osc("sine", f);
  const d1 = n.gain();
  const i1 = f * (0.6 + 1.2 * bright);
  d1.gain.setValueAtTime(i1, t);
  d1.gain.setTargetAtTime(f * 0.15, t, 0.5);
  m1.connect(d1).connect(c1.frequency);
  const g1 = n.gain(0.62);
  c1.connect(g1).connect(amp);

  // Tine: carrier f, modulator 7f, index decays fast. Harmonic (1 ± 7k) so it stays in tune.
  if (f * 8 < n.nyquist) {
    const c2 = n.osc("sine", f);
    const m2 = n.osc("sine", f * 7);
    const d2 = n.gain();
    d2.gain.setValueAtTime(f * 7 * (0.25 + 0.55 * bright), t);
    d2.gain.setTargetAtTime(0, t, 0.09);
    m2.connect(d2).connect(c2.frequency);
    const g2 = n.gain();
    n.perc(g2, 0.32, 0.002, 0.35);
    c2.connect(g2).connect(amp);
  }

  // Shimmer: a slightly sharp pure fundamental beating slowly against the body, plus a soft octave.
  const c3 = n.osc("sine", f * cents(3.5));
  const g3 = n.gain(0.22);
  c3.connect(g3).connect(amp);
  n.partial(out, f * 2 * cents(-2), 0.08 * bright, 0.006, 0.7);
  return n.finish();
};

// ---------------------------------------------------------------------------------------
// pluck: warm plucked string. Two detuned band-limited saws plus a triangle through a
// lowpass whose cutoff snaps shut, like a string losing its upper harmonics.
// (A Karplus-Strong loop is not usable here: Web Audio clamps a DelayNode in a cycle to
// at least one render quantum, ~2.7 ms, so anything above ~370 Hz would go flat.)
const pluck: Recipe = (v, out, f, t, vel, bright) => {
  const n = new Note(v, t);
  const lp = n.filter("lowpass", f * (3 + 9 * bright), 1.2);
  lp.frequency.setTargetAtTime(Math.max(f * 1.6, 180), t + 0.004, 0.09 + 0.05 * bright);
  const lp2 = n.filter("lowpass", Math.min(7000, f * 12), 0.5);
  const amp = n.gain();
  n.bump(n.perc(amp, 1, 0.003, 0.42));
  lp.connect(lp2).connect(amp).connect(out);

  const s1 = n.osc("sawtooth", f, -5);
  const s2 = n.osc("sawtooth", f, 5);
  const tri = n.osc("triangle", f);
  const gs = n.gain(0.3);
  const gt = n.gain(0.75);
  s1.connect(gs);
  s2.connect(gs);
  gs.connect(lp);
  tri.connect(gt).connect(lp);

  // Pick noise: a few milliseconds of bandpassed noise at the start.
  const nz = n.noise(0.02);
  const bp = n.filter("bandpass", Math.min(f * 6, 5000), 1.2);
  const gn = n.gain();
  n.perc(gn, 0.12 * (0.4 + bright), 0.001, 0.006);
  nz.connect(bp).connect(gn).connect(out);
  return n.finish();
};

// ---------------------------------------------------------------------------------------
// marimba: sine fundamental, tuned 4th partial (3.98) that dies quickly, a faint 10th
// partial and a woody mallet click.
const marimba: Recipe = (v, out, f, t, vel, bright) => {
  const n = new Note(v, t);
  n.partial(out, f, 1, 0.002, 0.34);
  n.partial(out, f * 3.98, 0.32 * (0.5 + bright), 0.001, 0.055);
  n.partial(out, f * 9.9, 0.06 * bright, 0.001, 0.02);
  const nz = n.noise(0.03);
  const bp = n.filter("bandpass", Math.min(1800 + f * 2, 5000), 1.4);
  const gn = n.gain();
  n.perc(gn, 0.25 * (0.4 + bright), 0.0008, 0.006);
  nz.connect(bp).connect(gn).connect(out);
  return n.finish();
};

// ---------------------------------------------------------------------------------------
// kalimba: sine with inharmonic tine partials (5.4x, 11.1x) decaying fast, and a soft
// thumb-noise attack.
const kalimba: Recipe = (v, out, f, t, vel, bright) => {
  const n = new Note(v, t);
  n.partial(out, f, 1, 0.002, 0.55);
  n.partial(out, f * 2, 0.06, 0.002, 0.18);
  n.partial(out, f * 5.4, 0.2 * (0.4 + bright), 0.001, 0.05);
  n.partial(out, f * 11.1, 0.07 * bright, 0.001, 0.025);
  const nz = n.noise(0.04);
  const lp = n.filter("lowpass", 1100, 0.7);
  const gn = n.gain();
  n.perc(gn, 0.28, 0.004, 0.012);
  nz.connect(lp).connect(gn).connect(out);
  return n.finish();
};

// ---------------------------------------------------------------------------------------
// koto: bright plucked silk string. Triangle + saw blend, pitch starts 20 cents sharp and
// settles within ~80 ms. A lowpass body path plus a nasal bandpass path that fades fast.
const koto: Recipe = (v, out, f, t, vel, bright) => {
  const n = new Note(v, t);
  const tri = n.osc("triangle", f);
  const saw = n.osc("sawtooth", f);
  for (const o of [tri, saw]) {
    o.frequency.setValueAtTime(f * cents(20), t);
    o.frequency.exponentialRampToValueAtTime(f, t + 0.09);
  }
  const mix = n.gain(1);
  const gSaw = n.gain(0.4);
  tri.connect(mix);
  saw.connect(gSaw).connect(mix);

  const lp = n.filter("lowpass", f * (4 + 6 * bright), 0.9);
  lp.frequency.setTargetAtTime(f * 2.2, t + 0.01, 0.25);
  const bp = n.filter("bandpass", Math.min(f * 3, 4500), 1.6);
  const gBody = n.gain(0.9);
  const gNasal = n.gain();
  n.perc(gNasal, 0.9 * bright, 0.001, 0.12);
  mix.connect(lp).connect(gBody);
  mix.connect(bp).connect(gNasal);

  const amp = n.gain();
  n.bump(n.perc(amp, 1, 0.002, 0.55));
  gBody.connect(amp);
  gNasal.connect(amp);
  amp.connect(out);

  const nz = n.noise(0.015);
  const hp = n.filter("highpass", 2500, 0.7);
  const gn = n.gain();
  n.perc(gn, 0.07 * bright, 0.0005, 0.004);
  nz.connect(hp).connect(gn).connect(out);
  return n.finish();
};

// ---------------------------------------------------------------------------------------
// flute: breathy sine/triangle with a slow attack, vibrato that fades in after 200 ms,
// and a filtered-noise breath layer.
const flute: Recipe = (v, out, f, t, vel, bright) => {
  const n = new Note(v, t);
  const hold = 0.5;
  const amp = n.gain();
  amp.gain.setValueAtTime(0, t);
  amp.gain.linearRampToValueAtTime(1, t + 0.08);
  amp.gain.setTargetAtTime(0.8, t + 0.08, 0.2);
  amp.gain.setTargetAtTime(0, t + hold, 0.22);
  n.bump(t + hold + 0.22 * 8);
  amp.connect(out);

  const s = n.osc("sine", f);
  const tr = n.osc("triangle", f);
  const gs = n.gain(0.85);
  const gt = n.gain(0.22 + 0.2 * bright);
  s.connect(gs).connect(amp);
  tr.connect(gt).connect(amp);

  const lfo = n.osc("sine", 5.1);
  const depth = n.gain(0);
  depth.gain.setValueAtTime(0, t + 0.2);
  depth.gain.linearRampToValueAtTime(f * 0.0035, t + 0.55);
  lfo.connect(depth);
  depth.connect(s.frequency);
  depth.connect(tr.frequency);

  const nz = n.noise(hold + 1.8);
  const bp = n.filter("bandpass", Math.min(f * 2, 6000), 1.1);
  const hp = n.filter("highpass", 600, 0.7);
  const gb = n.gain();
  gb.gain.setValueAtTime(0, t);
  gb.gain.linearRampToValueAtTime(0.16 * (0.5 + bright), t + 0.04);
  gb.gain.setTargetAtTime(0.045, t + 0.05, 0.12);
  gb.gain.setTargetAtTime(0, t + hold, 0.2);
  nz.connect(bp).connect(hp).connect(gb).connect(out);
  return n.finish();
};

// ---------------------------------------------------------------------------------------
// celesta: music-box. Pairs of pure sines a few cents apart, short, with a faint octave pair.
const celesta: Recipe = (v, out, f, t, vel, bright) => {
  const n = new Note(v, t);
  n.partial(out, f, 0.55, 0.0015, 0.36);
  n.partial(out, f * cents(4), 0.45, 0.0015, 0.3);
  n.partial(out, f * 2 * cents(-3), 0.1 * (0.4 + bright), 0.001, 0.09);
  n.partial(out, f * 2 * cents(2), 0.08 * (0.4 + bright), 0.001, 0.07);
  n.partial(out, f * 4, 0.03 * bright, 0.001, 0.03);
  return n.finish();
};

// ---------------------------------------------------------------------------------------
// ghost: a soft vowel. Two detuned triangles through a pair of formant bandpasses (a
// random vowel per note) plus a lowpassed body so the pitch stays anchored.
const VOWELS: readonly (readonly [number, number])[] = [
  [730, 1090], // ah
  [570, 840], // oh
  [300, 870], // oo
  [530, 1840], // eh
];
const ghost: Recipe = (v, out, f, t, vel, bright) => {
  const n = new Note(v, t);
  const hold = 0.45;
  const amp = n.gain();
  amp.gain.setValueAtTime(0, t);
  amp.gain.linearRampToValueAtTime(1, t + 0.14);
  amp.gain.setTargetAtTime(0, t + hold, 0.32);
  n.bump(t + hold + 0.32 * 8);
  amp.connect(out);

  const a = n.osc("triangle", f, -5);
  const b = n.osc("triangle", f, 5);
  const mix = n.gain(0.5);
  a.connect(mix);
  b.connect(mix);

  // The vowel glides between two random vowels over the note ("oo-ah", "oh-eh"...). The
  // glide sounds like a voice and also evens out loudness between vowels.
  const va = VOWELS[Math.floor(Math.random() * VOWELS.length)] ?? VOWELS[0]!;
  const vb = VOWELS[Math.floor(Math.random() * VOWELS.length)] ?? VOWELS[1]!;
  const body = n.filter("lowpass", Math.max(f * 1.8, 250), 0.6);
  const f1 = n.filter("bandpass", va[0], 2.5);
  const f2 = n.filter("bandpass", va[1], 3.5);
  f1.frequency.linearRampToValueAtTime(vb[0], t + hold + 0.4);
  f2.frequency.linearRampToValueAtTime(vb[1], t + hold + 0.4);
  const gBody = n.gain(0.85);
  const g1 = n.gain(0.8);
  const g2 = n.gain(0.6 * (0.5 + bright));
  mix.connect(body).connect(gBody).connect(amp);
  mix.connect(f1).connect(g1).connect(amp);
  mix.connect(f2).connect(g2).connect(amp);
  return n.finish();
};

export const RECIPES: Record<VoiceName, Recipe> = { glass, pluck, marimba, kalimba, koto, flute, celesta, ghost };

/**
 * Mix settings per voice: base level (balanced by offline RMS measurement), reverb and
 * delay send amounts.
 */
export interface VoiceMix {
  level: number;
  reverb: number;
  delay: number;
}

export const VOICE_MIX: Record<VoiceName, VoiceMix> = {
  glass: { level: 0.42, reverb: 0.38, delay: 0.14 },
  pluck: { level: 0.44, reverb: 0.28, delay: 0.22 },
  marimba: { level: 0.44, reverb: 0.26, delay: 0.1 },
  kalimba: { level: 0.33, reverb: 0.32, delay: 0.18 },
  koto: { level: 0.34, reverb: 0.32, delay: 0.16 },
  flute: { level: 0.24, reverb: 0.46, delay: 0.12 },
  celesta: { level: 0.42, reverb: 0.46, delay: 0.24 },
  ghost: { level: 0.31, reverb: 0.6, delay: 0.08 },
};
